import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { registerServiceWorker, subscribeToPush } from './pushNotifications';

// Pages
import SplashScreen from './pages/SplashScreen';
import AuthPage from './pages/AuthPage';
import ActivatePage from './pages/ActivatePage';
import WelcomePage from './pages/WelcomePage';
import DonatePage from './pages/DonatePage';
import OnboardingTutorial from './pages/OnboardingTutorial';
import Dashboard from './pages/Dashboard';
import CircleManagement from './pages/CircleManagement';
import CircleDetail from './pages/CircleDetail';
import ResourceHub from './pages/ResourceHub';
import NotificationsPage from './pages/NotificationsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import MedicalProvidersPage from './pages/MedicalProvidersPage';
import CalendarPage from './pages/CalendarPage';
import CalendarPageStandalone from './pages/CalendarPageStandalone';
import EmergencyPlanPage from './pages/EmergencyPlanPage';
import TasksPage from './pages/TasksPage';
import ProfilePage from './pages/ProfilePage';
import PhotoGalleryPage from './pages/PhotoGalleryPage';
import NotificationControlsPage from './pages/NotificationControlsPage';
import RecurringTasksPage from './pages/RecurringTasksPage';
import PlanWeekPage from './pages/PlanWeekPage';
import PatientOnboarding from './pages/PatientOnboarding';
import SupportOnboarding from './pages/SupportOnboarding';
import CircleHome from './pages/CircleHome';
import LandingPage from './pages/LandingPage';
import BillingPage from './pages/BillingPage';
import BillingSuccess from './pages/BillingSuccess';
import GiftPage from './pages/GiftPage';
import GiftIntroPage from './pages/GiftIntroPage';
import GiftCoachingPage from './pages/GiftCoachingPage';
import CoachPage from './pages/CoachPage';
import GiftSuccess from './pages/GiftSuccess';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import CareProfilePage from './pages/CareProfilePage';
import PublicCareProfilePage from './pages/PublicCareProfilePage';
import PartnerPage from './pages/PartnerPage';
import AuthCallback from './pages/AuthCallback';
import BottomNav from './components/BottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enable cookie-based auth for all requests
axios.defaults.withCredentials = true;

// CSRF: read csrf_token cookie and send as header on every request
axios.interceptors.request.use((config) => {
  const csrfToken = document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1];
  if (csrfToken) config.headers['x-csrf-token'] = csrfToken;
  return config;
});

// Normalize FastAPI 422 validation errors: detail arrays -> readable string (prevents React child crashes in toasts)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail !== 'string') {
      error.response.data.detail = Array.isArray(detail)
        ? detail.map(d => (d && d.msg) ? `${(d.loc || []).slice(1).join('.')}: ${d.msg}` : String(d)).join('; ')
        : JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext();

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

// Layout with BottomNav
const AppLayout = ({ children, user }) => {
  const location = useLocation();
  const hideNav = ['/', '/individuals', '/auth'].includes(location.pathname);
  // OAuth callback interception: if the URL fragment contains session_id,
  // render the callback view instead of the normal routes. Reading from
  // useLocation().hash keeps this reactive after history.replaceState.
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      {children}
      {user && !hideNav && <BottomNav />}
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // CRITICAL (Emergent Google Auth): if we're on the OAuth callback,
    // skip the /auth/me check and let AuthCallback exchange the session_id first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
    // Apply saved theme on mount
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);

      // Register service worker & subscribe to push
      registerServiceWorker().then(() => subscribeToPush());
    } catch (error) {
      console.warn('Auth check: not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const [onboardingKey, setOnboardingKey] = useState(0);
  const [pendingInviteCode, setPendingInviteCode] = useState('');

  const login = (userData, isNewUser = false, inviteCode = '') => {
    setUser(userData);
    // Care Coordinators get their circle auto-created at signup and land on /welcome,
    // so skip the SupportOnboarding overlay for them.
    if (isNewUser && userData?.role !== 'care_coordinator') {
      setPendingInviteCode(inviteCode);
      setShowOnboarding(true);
    }
    // Register SW & subscribe after login
    registerServiceWorker().then(() => subscribeToPush());
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) { console.warn('Logout API call failed:', e); }
    setUser(null);
    localStorage.removeItem('hasSeenOnboarding');
  };

  const authContextValue = useMemo(() => ({ user, login, logout, fetchUser }), [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <BrowserRouter>
        <div className="App min-h-screen">
          <Toaster position="top-right" richColors />

          {showOnboarding && user && (
            <SupportOnboarding inviteCode={pendingInviteCode} onComplete={() => {
              setShowOnboarding(false);
              localStorage.setItem('hasSeenOnboarding', 'true');
              setPendingInviteCode('');
              setOnboardingKey(k => k + 1);
              // Patients: after the create-circle wizard, offer them the /welcome Calendly step.
              if (user?.role === 'patient') {
                setTimeout(() => { window.location.href = '/welcome'; }, 50);
              }
            }} />
          )}

          <AppLayout user={user}>
            <Routes>
              <Route path="/" element={!user ? <PartnerPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="/individuals" element={!user ? <LandingPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="/home" element={!user ? <SplashScreen /> : <Navigate to="/dashboard" replace />} />
              <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="/activate" element={!user ? <ActivatePage /> : <Navigate to="/dashboard" replace />} />
              <Route path="/donate" element={<DonatePage />} />
              <Route path="/donate/thanks" element={<DonatePage />} />
              <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
              <Route path="/patient-onboarding" element={!user ? <PatientOnboarding /> : <Navigate to="/dashboard" replace />} />
              <Route path="/gift" element={<GiftPage />} />
              <Route path="/care-profile-quiz" element={<PublicCareProfilePage />} />
              <Route path="/partner" element={<PartnerPage />} />
              <Route path="/gift-intro" element={<GiftIntroPage />} />
              <Route path="/gift-coaching" element={<GiftCoachingPage />} />
              <Route path="/coach" element={<CoachPage />} />
              <Route path="/gift/success" element={<GiftSuccess />} />
              <Route path="/dashboard" element={<ProtectedRoute><CircleHome key={onboardingKey} /></ProtectedRoute>} />
              <Route path="/circles" element={<ProtectedRoute><CircleManagement /></ProtectedRoute>} />
              <Route path="/circles/:circleId" element={<ProtectedRoute><CircleDetail /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><CalendarPageStandalone /></ProtectedRoute>} />
              <Route path="/resources" element={<ProtectedRoute><ResourceHub /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/photos" element={<ProtectedRoute><PhotoGalleryPage /></ProtectedRoute>} />
              <Route path="/notification-settings" element={<ProtectedRoute><NotificationControlsPage /></ProtectedRoute>} />
              <Route path="/recurring-tasks" element={<ProtectedRoute><RecurringTasksPage /></ProtectedRoute>} />
              <Route path="/plan-week" element={<ProtectedRoute><PlanWeekPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/care-profile" element={<ProtectedRoute><CareProfilePage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
              <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/circles/:circleId/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
              <Route path="/circles/:circleId/medical-providers" element={<ProtectedRoute><MedicalProvidersPage /></ProtectedRoute>} />
              <Route path="/circles/:circleId/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
              <Route path="/circles/:circleId/emergency-plan" element={<ProtectedRoute><EmergencyPlanPage /></ProtectedRoute>} />
            </Routes>
          </AppLayout>
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
export { API, BACKEND_URL };
